<template>
  <div id="app" :class="bodyClasses">
    <TheHeader
      @toggle-main-sidebar="toggleMainSidebar"
      @toggle-search="searchVisible = !searchVisible"
      :search-visible="searchVisible"
    />

    <div id="global-messages" v-if="errors">
      <BaseAlert
        v-if="errors"
        :content="errors"
        type="error"
        @dismiss-alert="clearGlobalErrors"
      />
    </div>

    <transition name="slide-fade">
      <QuickAlert
        v-if="notification"
        :type="notification.type"
        :content="notification.content"
      />
    </transition>

    <TheMainSidebar
      @toggle-main-sidebar="toggleMainSidebar"
      :visible="mainSidebarVisible"
      v-if="authenticated"
    />

    <main class="pt-5">
      <div class="main-content-wraper">
        <router-view
          @toggle-search="searchVisible = !searchVisible"
          :search-visible="searchVisible"
        />
      </div>

      <TheFooter
        class="mt-5"
      />
    </main>

    <!-- PAGE OVERLAY -->
    <div class="page-overlay"></div> <!-- PAGE OVERLAY -->
  </div>
</template>

<script>

import { registerVuexModule } from '@/utils'
import { mapState, mapActions } from 'vuex'
import AuthStore from '@/modules/auth/store'
import UsersStore from '@/modules/users/store'
import PostsStore from '@/modules/posts/store'
import UsersService from '@/modules/users/services/users-service'
import seo from '@/mixins/seo'

import BaseAlert from '@/components/common/Alert/BaseAlert'
import QuickAlert from '@/components/common/Alert/QuickAlert'
import TheFooter from '@/components/footer/TheFooter'
import TheHeader from '@/components/header/TheHeader'
import TheMainSidebar from '@/components/sidebar/TheMainSidebar'

export default {
  name: 'App',
  mixins: [seo],
  metaInfo () {
    return {
      title: this.seoDefaultTitle,
      titleTemplate: `%s | ${this.seoDefaultTitleAppend}`,
      meta: [
        { vmid: 'description', name: 'description', content: this.seoDefaultDescription },
        { vmid: 'og-description', name: 'og:description', content: this.seoDefaultDescription },
        { vmid: 'og-title', name: 'og:title', content: this.seoDefaultTitle },
        { vmid: 'og-url', name: 'og:url', content: this.currentUrl },
        { vmid: 'og-image', name: 'og:image', content: this.seoDefaultImage },
        { vmid: 'og-type', name: 'og:type', content: 'website' }
      ]
    }
  },
  data () {
    return {
      mainSidebarVisible: false,
      searchVisible: false,
      currentLocation: ''
    }
  },
  mounted () {
    if (localStorage.getItem('nightMode') !== null) {
      this.setNightMode({ status: localStorage.getItem('nightMode') === 'true' })
    } else {
      this.setNightMode({ status: false })
    }
  },
  watch: {
    nightMode (newValue, oldValue) {
      if (newValue) {
        document.querySelector('body').classList.add('night-mode')
      } else {
        document.querySelector('body').classList.remove('night-mode')
      }
    }
  },
  components: {
    BaseAlert,
    QuickAlert,
    TheHeader,
    TheFooter,
    TheMainSidebar
  },
  created () {
    registerVuexModule(this.$store, 'auth', AuthStore)
    registerVuexModule(this.$store, 'users', UsersStore)
    registerVuexModule(this.$store, 'posts', PostsStore)
    // Get the logged user when the page first loads
    this.getLoggedUser()
  },
  computed: {
    currentUrl () {
      return `${process.env.VUE_APP_BASE_URL}${this.$route.fullPath}`
    },
    ...mapState('auth', {
      authenticated: state => state.authenticated
    }),
    ...mapState({
      errors: state => state.errors,
      notification: state => state.notification,
      isMainSidebarCollapsed: state => state.isMainSidebarCollapsed,
      nightMode: state => state.nightMode
    }),
    isContentReadCreationPage () {
      const routeName = this.$route.name
      const routes = ['posts.create', 'posts.edit', 'posts.show', 'books.create', 'books.edit', 'chapters.show']
      return routes.includes(routeName)
    },
    bodyClasses () {
      return {
        logged: this.authenticated,
        'nav-open': this.mainSidebarVisible,
        'search-open': this.searchVisible,
        'sidebar-drawer': this.isContentReadCreationPage,
        night: this.nightMode
      }
    }
  },
  methods: {
    ...mapActions(['clearErrors', 'setMainSidebarCollapseStatus', 'setNightMode']),
    clearGlobalErrors () {
      this.clearErrors()
    },
    async getLoggedUser () {
      // Check if the user is authenticated and if the user in the store is filled
      if (AuthStore.state.authenticated) {
        if (!UsersStore.state.user) {
          try {
            await UsersService.me()
          } catch (error) {
            error.handleGlobally && error.handleGlobally()
          }
        }
      }
    },
    toggleOnApp (value) {
      this.nightMode = value
    },
    onClose () {
    },
    toggleMainSidebar () {
      this.mainSidebarVisible = !this.mainSidebarVisible
      this.setMainSidebarCollapseStatus({ status: !this.isMainSidebarCollapsed })
    }
  }
}
</script>

<style lang="scss" scoped>

#global-messages {
  position: fixed;
  top: 120px;
  left: calc(50% - 140px);
  width: 280px;
  z-index: 10;

  @include tablet {
    width: 500px;
    left: calc(50% - 250px);
  }
}

main {

  .main-content-wraper {
    min-height: calc(100vh - 80px - 300px);
    @include desktop {
      min-height: calc(100vh - 80px - 400px);
    }
  }
}

.search-open {
  ::v-deep .page-overlay {
    pointer-events: auto;
    opacity: 1;
    background: rgba(255,255,255,0.9);
  }

  ::v-deep #search-box {
    opacity: 1;
    pointer-events: auto;
  }
}

</style>
